import { Recipe, RecipeDifficulty } from "../models/recipe";

// Sort options shown in the SortBottomSheet and applied to recipe listings.
// Ordered by purpose, most-commonly-used first: recency, then name, then the
// attribute sorts (time, difficulty). Each dimension's pair stays adjacent.
export const RECIPE_SORT_OPTIONS = [
  "Recently added",
  "Most recently cooked",
  "Most cooked",
  "Alphabetical (A-Z)",
  "Quickest first",
  "Longest first",
  "Easiest first",
  "Most difficult first",
  "Highest rated",
] as const;

export type RecipeSortOption = (typeof RECIPE_SORT_OPTIONS)[number];

export const DEFAULT_SORT: RecipeSortOption = "Recently added";
export const ALPHABETICAL: RecipeSortOption = "Alphabetical (A-Z)";

// A collection's manual drag order. Not a real comparator — sortRecipes leaves the
// input untouched for it, and the input (selectRecipesByCollection) is already in
// manual order. Only offered on the collection screen.
export const CUSTOM_ORDER = "Custom order";
export const COLLECTION_SORT_OPTIONS = [CUSTOM_ORDER, ...RECIPE_SORT_OPTIONS] as const;

// The sort *value* is the stored identifier (persisted + compared in code), so it
// stays English. This maps each value to its i18n key for display only — screens
// render `t(SORT_LABEL_KEYS[value])`, keeping logic and persistence on the value.
export const SORT_LABEL_KEYS: Record<string, string> = {
  "Recently added": "sort.recentlyAdded",
  "Most recently cooked": "sort.mostRecentlyCooked",
  "Most cooked": "sort.mostCooked",
  "Alphabetical (A-Z)": "sort.alphabetical",
  "Quickest first": "sort.quickest",
  "Longest first": "sort.longest",
  "Easiest first": "sort.easiest",
  "Most difficult first": "sort.mostDifficult",
  "Highest rated": "sort.highestRated",
  "Custom order": "sort.customOrder",
};

// These options both filter (cooked only) and need external cook data (times /
// counts), so they're handled outside the pure Recipe comparators below. Exported
// so screens can detect them (e.g. to show a "no cooked recipes" empty state).
export const MOST_RECENTLY_COOKED: RecipeSortOption = "Most recently cooked";
export const MOST_COOKED: RecipeSortOption = "Most cooked";

// True for the sorts that filter to cooked-only recipes (so screens can share
// one "no cooked recipes yet" empty state instead of listing each option).
export const isCookedOnlySort = (option: string): boolean =>
  option === MOST_RECENTLY_COOKED || option === MOST_COOKED;

const difficultyRank: Record<string, number> = {
  [RecipeDifficulty.Easy]: 0,
  [RecipeDifficulty.Medium]: 1,
  [RecipeDifficulty.Hard]: 2,
};

// Recipes missing a duration sort to the end regardless of direction.
const dur = (r: Recipe) => r.cookingDuration ?? null;

// Difficulty rank; N/A (unset) ranks 0, i.e. with the easiest end.
const rank = (r: Recipe) => (r.difficulty ? (difficultyRank[r.difficulty] ?? 0) : 0);

// Sort key for a title that ignores leading quotes/brackets/punctuation, so
// '"Sizzling Beef" Steak Fried Rice' sorts under S, not at the very top.
export const recipeTitleSortKey = (title?: string | null): string =>
  (title ?? "").replace(/^[^\p{L}\p{N}]+/u, "").trim();

export const compareRecipeTitles = (a?: string | null, b?: string | null): number =>
  recipeTitleSortKey(a).localeCompare(recipeTitleSortKey(b), undefined, {
    sensitivity: "base",
  });

// "Most recently cooked" is handled separately (it filters + needs cook times),
// so the pure comparators cover every *other* option.
const comparators: Partial<Record<RecipeSortOption, (a: Recipe, b: Recipe) => number>> = {
  "Quickest first": (a, b) => (dur(a) ?? Infinity) - (dur(b) ?? Infinity),
  "Longest first": (a, b) => (dur(b) ?? -Infinity) - (dur(a) ?? -Infinity),
  "Easiest first": (a, b) => rank(a) - rank(b),
  "Most difficult first": (a, b) => rank(b) - rank(a),
  // Highest rating first; unrated recipes sort to the end.
  "Highest rated": (a, b) => (b.rating ?? -Infinity) - (a.rating ?? -Infinity),
  // ISO timestamps compare chronologically as strings; newest first.
  "Recently added": (a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""),
  "Alphabetical (A-Z)": (a, b) => compareRecipeTitles(a.title, b.title),
};

// Returns a new sorted array (never mutates the input). The cooked-only sorts
// additionally filter to cooked recipes and order by external cook data, so
// callers pass a recipeId -> latest-cook ISO map (see selectLastCookedByRecipe)
// and a recipeId -> cook-count map (see selectCookCountByRecipe).
export function sortRecipes<T extends Recipe>(
  recipes: T[],
  option: string,
  lastCookedAt: Record<string, string> = {},
  cookCounts: Record<string, number> = {},
): T[] {
  if (option === MOST_RECENTLY_COOKED) {
    // ISO timestamps compare chronologically as strings; missing → sorts last.
    return recipes
      .filter((r) => r.cooked)
      .sort((a, b) =>
        (lastCookedAt[b.id] ?? "").localeCompare(lastCookedAt[a.id] ?? ""),
      );
  }
  if (option === MOST_COOKED) {
    // Highest cook count first; ties broken by most-recently cooked (matches the
    // home "Cook it again" ordering).
    return recipes
      .filter((r) => r.cooked)
      .sort(
        (a, b) =>
          (cookCounts[b.id] ?? 0) - (cookCounts[a.id] ?? 0) ||
          (lastCookedAt[b.id] ?? "").localeCompare(lastCookedAt[a.id] ?? ""),
      );
  }
  const compare = comparators[option as RecipeSortOption];
  return compare ? [...recipes].sort(compare) : recipes;
}

// For the cooked-only sorts, split all recipes into the cooked list (in the sort's
// order) and the never-cooked remainder (ordered by "Recently added"), so a screen
// can show every recipe under a "Never cooked" divider instead of hiding the
// uncooked ones. For any other option, everything lands in `cooked` (no split), so
// callers can always use this and render the divider only when `neverCooked` is
// non-empty.
export function splitByCooked<T extends Recipe>(
  recipes: T[],
  option: string,
  lastCookedAt: Record<string, string> = {},
  cookCounts: Record<string, number> = {},
): { cooked: T[]; neverCooked: T[] } {
  const cooked = sortRecipes(recipes, option, lastCookedAt, cookCounts);
  if (!isCookedOnlySort(option)) return { cooked, neverCooked: [] };
  const cookedIds = new Set(cooked.map((r) => r.id));
  const neverCooked = sortRecipes(
    recipes.filter((r) => !cookedIds.has(r.id)),
    DEFAULT_SORT,
    lastCookedAt,
    cookCounts,
  );
  return { cooked, neverCooked };
}
